import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import {
  Collection,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  ListLayout,
  Virtualizer,
} from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgDelete } from '@actual-app/components/icons/v0';
import {
  SvgCheveronDown,
  SvgCheveronRight,
  SvgDotsHorizontalTriple,
} from '@actual-app/components/icons/v1';
import type { MenuItem } from '@actual-app/components/menu';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { validForMerge } from '@actual-app/core/shared/merge';
import * as monthUtils from '@actual-app/core/shared/months';
import { isPreviewId } from '@actual-app/core/shared/transactions';
import { validForTransfer } from '@actual-app/core/shared/transfer';
import { groupById, integerToCurrency } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type {
  CategoryEntity,
  TransactionEntity,
} from '@actual-app/core/types/models';

import { FloatingActionBar } from '#components/mobile/FloatingActionBar';
import { HuecoBarra } from '#components/mobile/HuecoBarra';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { useOpcionesPantalla } from '#components/mobile/opciones/OpcionesPantalla';
import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { Boton } from '#components/mobile/ui/Boton';
import { Cargando } from '#components/mobile/ui/Cargando';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { HojaMenu } from '#components/mobile/ui/HojaMenu';
import { Importe } from '#components/mobile/ui/Importe';
import { useRecordarScrollDe } from '#components/mobile/ui/recordarScroll';
import {
  estiloSeparadorFecha,
  textoSeparadorFecha,
} from '#components/mobile/ui/SeparadorFecha';
import { espacio } from '#components/mobile/ui/tokens';
import { useAccounts } from '#hooks/useAccounts';
import { useCategoriesById } from '#hooks/useCategories';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { usePayees } from '#hooks/usePayees';
import { useScrollListener } from '#hooks/useScrollListener';
import { useSelectedDispatch, useSelectedItems } from '#hooks/useSelected';
import { useTransactionBatchActions } from '#hooks/useTransactionBatchActions';
import { useUndo } from '#hooks/useUndo';

import {
  agruparPorDia,
  guardarProximosAbierto,
  leerProximosAbierto,
  tocarSelecciona,
  totalProximos,
} from './listaMovimientos';
import { ROW_HEIGHT, TransactionListItem } from './TransactionListItem';

type LoadingProps = {
  style?: CSSProperties;
  'aria-label': string;
};

function Loading({ style, 'aria-label': ariaLabel }: LoadingProps) {
  const { t } = useTranslation();
  return (
    <View
      aria-label={ariaLabel || t('Loading...')}
      style={{ flex: 1, ...style }}
    >
      <Cargando />
    </View>
  );
}

type TransactionListProps = {
  isLoading: boolean;
  transactions: readonly TransactionEntity[];
  showRunningBalances?: boolean;
  runningBalances?: Map<TransactionEntity['id'], IntegerAmount>;
  onOpenTransaction?: (transaction: TransactionEntity) => void;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  showMakeTransfer?: boolean;
  isReconciling?: boolean;
  onToggleTransactionCleared?: (transaction: TransactionEntity) => void;
  /** Texto buscado (H63): cambia el estado vacío por «Nada coincide…». */
  textoBusqueda?: string;
  onBorrarBusqueda?: () => void;
  /** B8 · H13: «Seleccionar» en la brújula (Gastos y Cuenta). */
  conSeleccionar?: boolean;
};

export function TransactionList({
  isLoading,
  transactions,
  showRunningBalances,
  runningBalances,
  onOpenTransaction,
  isLoadingMore,
  onLoadMore,
  showMakeTransfer = false,
  isReconciling = false,
  onToggleTransactionCleared,
  textoBusqueda,
  onBorrarBusqueda,
  conSeleccionar = false,
}: TransactionListProps) {
  const locale = useLocale();
  const { t } = useTranslation();
  const hoy = monthUtils.currentDay();
  const { proximos, secciones } = useMemo(
    () => agruparPorDia(transactions, hoy),
    [transactions, hoy],
  );
  const [proximosAbierto, setProximosAbierto] = useState(leerProximosAbierto);
  const alternarProximos = useCallback(() => {
    setProximosAbierto(abierto => {
      guardarProximosAbierto(!abierto);
      return !abierto;
    });
  }, []);
  const proximosVisibles = useMemo(
    () => proximos.filter(t => !t.is_child),
    [proximos],
  );
  const sections = useMemo(
    () => [
      ...(proximosVisibles.length > 0
        ? [
            {
              id: 'proximos',
              date: '',
              esProximos: true,
              transactions: proximosAbierto ? proximosVisibles : [],
            },
          ]
        : []),
      ...secciones.map(s => ({ ...s, esProximos: false })),
    ],
    [proximosVisibles, proximosAbierto, secciones],
  );
  const busqueda = textoBusqueda?.trim() ?? '';

  const dispatchSelected = useSelectedDispatch();
  const selectedTransactions = useSelectedItems();

  // B8 · H13: «Seleccionar» de la brújula abre el modo selección sin tener
  // que adivinar la pulsación larga. Se cierra al vaciar la selección.
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const habiaSeleccion = useRef(false);
  useEffect(() => {
    if (habiaSeleccion.current && selectedTransactions.size === 0) {
      setModoSeleccion(false);
    }
    habiaSeleccion.current = selectedTransactions.size > 0;
  }, [selectedTransactions]);
  const seleccionando = modoSeleccion || selectedTransactions.size > 0;
  // «Atrás» del móvil sale del modo selección (no de la pantalla).
  useCerrarConAtras(seleccionando, () => {
    setModoSeleccion(false);
    dispatchSelected({ type: 'select-none' });
  });
  // Al volver con «atrás», la lista sigue donde estaba.
  const [lista, setLista] = useState<HTMLDivElement | null>(null);
  useRecordarScrollDe(lista, 'movimientos');
  useOpcionesPantalla([
    {
      id: 'seleccionar',
      tipo: 'propia',
      texto: t('Select (transactions)'),
      icono: 'check',
      onPress: () => setModoSeleccion(true),
      oculta: !conSeleccionar || seleccionando,
    },
  ]);

  const onTransactionPress: (
    transaction: TransactionEntity,
    isLongPress?: boolean,
  ) => void = useCallback(
    (transaction, isLongPress = false) => {
      if (
        tocarSelecciona({
          esPrevisto: isPreviewId(transaction.id),
          pulsacionLarga: isLongPress,
          modoSeleccion,
          seleccionados: selectedTransactions.size,
        })
      ) {
        dispatchSelected({ type: 'select', id: transaction.id });
      } else {
        onOpenTransaction?.(transaction);
      }
    },
    [dispatchSelected, onOpenTransaction, selectedTransactions, modoSeleccion],
  );

  useScrollListener(
    useCallback(
      ({ hasScrolledToEnd }) => {
        if (hasScrolledToEnd('down', 100)) {
          onLoadMore?.();
        }
      },
      [onLoadMore],
    ),
  );

  return (
    <View style={{ flex: 1 }}>
      {isLoading && (
        <Loading
          style={{ flex: 'none', paddingBottom: 8 }}
          aria-label={t('Loading transactions...')}
        />
      )}
      <View style={{ flex: 1 }}>
        <Virtualizer
          layout={ListLayout}
          layoutOptions={{
            estimatedRowHeight: ROW_HEIGHT,
            padding: 0,
          }}
        >
          <ListBox
            ref={setLista}
            aria-label={t('Transaction list')}
            selectionMode={seleccionando ? 'multiple' : 'single'}
            style={{ flex: 1, overflow: 'auto' }}
            selectedKeys={selectedTransactions}
            dependencies={[
              selectedTransactions,
              seleccionando,
              locale,
              onTransactionPress,
              runningBalances,
              showRunningBalances,
              isReconciling,
              onToggleTransactionCleared,
              proximosAbierto,
              alternarProximos,
              t,
            ]}
            renderEmptyState={() =>
              !isLoading &&
              (busqueda ? (
                <View style={{ padding: espacio.margen }}>
                  <EstadoVacio
                    ilustracion="barquito"
                    data-testid="busqueda-vacia"
                    titulo={t('Nothing matches "{{search}}"', {
                      search: busqueda,
                    })}
                    accion={
                      onBorrarBusqueda && (
                        <Boton variante="tonal" onPress={onBorrarBusqueda}>
                          <Trans>Clear search</Trans>
                        </Boton>
                      )
                    }
                  />
                </View>
              ) : (
                <View style={{ padding: espacio.margen }}>
                  <EstadoVacio
                    ilustracion="barquito"
                    titulo={<Trans>No transactions</Trans>}
                    texto={
                      <Trans>
                        Nothing here yet. Add one when you pay for something.
                      </Trans>
                    }
                  />
                </View>
              ))
            }
            items={sections}
          >
            {section => (
              <ListBoxSection>
                {section.esProximos ? (
                  <Header
                    style={{
                      ...estiloSeparadorFecha,
                      padding: 0,
                      width: '100%',
                    }}
                  >
                    <CabeceraProximos
                      cantidad={proximosVisibles.length}
                      total={totalProximos(proximosVisibles)}
                      abierto={proximosAbierto}
                      onPress={alternarProximos}
                    />
                  </Header>
                ) : (
                  <Header
                    style={{
                      ...estiloSeparadorFecha,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      position: 'sticky',
                      top: '0',
                      width: '100%',
                      zIndex: 10,
                    }}
                  >
                    {textoSeparadorFecha(section.date, locale, t)}
                  </Header>
                )}
                <Collection
                  items={section.transactions.filter(
                    t => !isPreviewId(t.id) || !t.is_child,
                  )}
                >
                  {transaction => (
                    <ListBoxItem textValue={transaction.id} value={transaction}>
                      {itemProps => (
                        <TransactionListItem
                          {...itemProps}
                          showRunningBalance={showRunningBalances}
                          runningBalance={runningBalances?.get(transaction.id)}
                          transaction={transaction}
                          isReconciling={isReconciling}
                          onPress={trans => onTransactionPress(trans)}
                          onLongPress={trans => onTransactionPress(trans, true)}
                          onToggleCleared={onToggleTransactionCleared}
                        />
                      )}
                    </ListBoxItem>
                  )}
                </Collection>
              </ListBoxSection>
            )}
          </ListBox>
        </Virtualizer>
      </View>

      {isLoadingMore && (
        <Loading
          aria-label={t('Loading more transactions...')}
          style={{
            // Same height as transaction list item
            height: ROW_HEIGHT,
          }}
        />
      )}

      {/* Hueco para que la barra de pestañas (fija) no tape la última fila. */}
      <HuecoBarra />

      {selectedTransactions.size > 0 && (
        <SelectedTransactionsFloatingActionBar
          transactions={transactions}
          showMakeTransfer={showMakeTransfer}
        />
      )}
      {modoSeleccion && selectedTransactions.size === 0 && (
        <FloatingActionBar
          style={{
            bottom: `calc(${MOBILE_NAV_HEIGHT - 10}px + env(safe-area-inset-bottom, 0px))`,
            zIndex: 101,
          }}
        >
          <View
            data-testid="modo-seleccion"
            style={{
              flex: 1,
              padding: '0 8px 0 14px',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <Text style={styles.mediumText}>
              <Trans>Tap transactions to select them</Trans>
            </Text>
            <Button
              variant="bare"
              data-testid="modo-seleccion-cancelar"
              onPress={() => setModoSeleccion(false)}
              style={{
                ...styles.mobileMenuItem,
                color: 'currentColor',
                minHeight: 44,
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </View>
        </FloatingActionBar>
      )}
    </View>
  );
}

type CabeceraProximosProps = {
  cantidad: number;
  total: number;
  abierto: boolean;
  onPress: () => void;
};

/**
 * Cabecera plegable de los pagos fijos futuros (H60): «Próximos (3) ·
 * −2.160 €». Cerrada por defecto; recuerda si la dejaste abierta.
 */
export function CabeceraProximos({
  cantidad,
  total,
  abierto,
  onPress,
}: CabeceraProximosProps) {
  const { t } = useTranslation();
  const Flecha = abierto ? SvgCheveronDown : SvgCheveronRight;
  return (
    <Button
      variant="bare"
      aria-expanded={abierto}
      data-testid="cabecera-proximos"
      onPress={onPress}
      style={{
        ...estiloSeparadorFecha,
        width: '100%',
        minHeight: 44,
        justifyContent: 'flex-start',
        gap: 6,
        borderRadius: 0,
      }}
    >
      <Flecha style={{ width: 14, height: 14, flexShrink: 0 }} />
      <Text style={{ fontWeight: 800 }}>
        {t('Upcoming ({{count}})', { count: cantidad })}
      </Text>
      <Text aria-hidden>·</Text>
      <Importe valor={total} style={{ fontWeight: 800 }} />
    </Button>
  );
}

type SelectedTransactionsFloatingActionBarProps = {
  transactions: readonly TransactionEntity[];
  style?: CSSProperties;
  showMakeTransfer: boolean;
};

function SelectedTransactionsFloatingActionBar({
  transactions,
  style = {},
  showMakeTransfer,
}: SelectedTransactionsFloatingActionBarProps) {
  const { t } = useTranslation();
  const [isEditMenuOpen, setIsEditMenuOpen] = useState(false);
  const [isMoreOptionsMenuOpen, setIsMoreOptionsMenuOpen] = useState(false);
  const selectedTransactions = useSelectedItems();
  const selectedTransactionsArray = Array.from(selectedTransactions);
  const dispatchSelected = useSelectedDispatch();

  const buttonProps = useMemo(
    () => ({
      style: {
        ...styles.mobileMenuItem,
        color: 'currentColor',
        height: styles.mobileMinHeight,
      },
      activeStyle: {
        color: 'currentColor',
      },
      hoveredStyle: {
        color: 'currentColor',
      },
    }),
    [],
  );

  const textoSeleccion = t('{{count}} transactions selected', {
    count: selectedTransactions.size,
  });

  const { showUndoNotification } = useUndo();
  const {
    onBatchEdit,
    onBatchDuplicate,
    onBatchDelete,
    onSetTransfer,
    onMerge,
  } = useTransactionBatchActions();

  const navigate = useNavigate();
  const { data: accounts = [] } = useAccounts();
  const accountsById = useMemo(() => groupById(accounts), [accounts]);

  const { data: payees = [] } = usePayees();
  const payeesById = useMemo(() => groupById(payees), [payees]);

  const {
    data: { list: categoriesById } = {
      list: {} as Record<string, CategoryEntity>,
    },
  } = useCategoriesById();

  const twoTransactions: [TransactionEntity, TransactionEntity] | undefined =
    useMemo(() => {
      // only two selected
      if (selectedTransactionsArray.length !== 2) {
        return undefined;
      }

      const [a, b] = selectedTransactionsArray.map(id =>
        transactions.find(t => t.id === id),
      );
      if (!a || !b) {
        return undefined;
      }

      return [a, b];
    }, [selectedTransactionsArray, transactions]);

  const canBeTransfer = useMemo(() => {
    if (!twoTransactions) {
      return false;
    }
    const [fromTrans, toTrans] = twoTransactions;
    return validForTransfer(fromTrans, toTrans);
  }, [twoTransactions]);

  const canMerge = useMemo(() => {
    return Boolean(
      twoTransactions && validForMerge(twoTransactions[0], twoTransactions[1]),
    );
  }, [twoTransactions]);

  const moreOptionsMenuItems: MenuItem<string>[] = [
    {
      name: 'duplicate',
      text: t('Duplicate'),
    },
    {
      name: 'delete',
      text: t('Delete'),
    },
    {
      name: 'merge',
      text: t('Merge'),
      disabled: !canMerge,
    },
  ];

  if (showMakeTransfer) {
    moreOptionsMenuItems.splice(2, 0, {
      name: 'transfer',
      text: t('Make transfer'),
      disabled: !canBeTransfer,
    });
  }

  return (
    // Encima de la barra de pestañas (fija), no sobre ella.
    <FloatingActionBar
      style={{
        bottom: `calc(${MOBILE_NAV_HEIGHT - 10}px + env(safe-area-inset-bottom, 0px))`,
        zIndex: 101,
        ...style,
      }}
    >
      <View
        data-testid="barra-seleccion"
        style={{
          flex: 1,
          padding: 8,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <Button
            variant="bare"
            data-testid="seleccion-quitar"
            {...buttonProps}
            style={{ ...buttonProps.style, marginRight: 4 }}
            onPress={() => {
              if (selectedTransactions.size > 0) {
                dispatchSelected({ type: 'select-none' });
              }
            }}
          >
            <SvgDelete width={10} height={10} />
          </Button>
          <Text style={styles.mediumText}>{textoSeleccion}</Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
          }}
        >
          <Button
            variant="bare"
            aria-label={t('Edit fields')}
            data-testid="seleccion-editar"
            onPress={() => {
              setIsEditMenuOpen(true);
            }}
            {...buttonProps}
          >
            <Trans>Edit</Trans>
          </Button>

          <HojaMenu
            isOpen={isEditMenuOpen}
            data-testid="hoja-editar-campos"
            titulo={t('Edit fields')}
            subtitulo={textoSeleccion}
            iconos={{
              account: 'bank',
              payee: 'store',
              notes: 'note',
              category: 'tag',
              cleared: 'check',
            }}
            onCerrar={() => setIsEditMenuOpen(false)}
            onMenuSelect={name => {
              void onBatchEdit?.({
                name,
                ids: selectedTransactionsArray,
                onSuccess: (ids, name, value, mode) => {
                  let displayValue;
                  switch (name) {
                    case 'account':
                      displayValue = accountsById[String(value)]?.name ?? value;
                      break;
                    case 'category':
                      displayValue =
                        categoriesById[String(value)]?.name ?? value;
                      break;
                    case 'payee':
                      displayValue = payeesById[String(value)]?.name ?? value;
                      break;
                    case 'amount':
                      displayValue = Number.isNaN(Number(value))
                        ? value
                        : integerToCurrency(Number(value));
                      break;
                    case 'notes':
                      displayValue = `${mode} with ${String(value)}`;
                      break;
                    default:
                      displayValue = value;
                      break;
                  }

                  showUndoNotification({
                    message: `Successfully updated ${name} of ${ids.length} transaction${ids.length > 1 ? 's' : ''} to [${String(displayValue)}](#${String(displayValue)}).`,
                    messageActions: {
                      [String(displayValue)]: () => {
                        switch (name) {
                          case 'account':
                            void navigate(`/accounts/${String(value)}`);
                            break;
                          case 'category':
                            void navigate(`/categories/${String(value)}`);
                            break;
                          case 'payee':
                            void navigate(`/payees`);
                            break;
                          default:
                            break;
                        }
                      },
                    },
                  });
                },
              });
              setIsEditMenuOpen(false);
            }}
            items={[
              // Add support later on.
              // {
              //   name: 'date',
              //   text: 'Date',
              // },
              {
                name: 'account',
                text: t('Account'),
              },
              {
                name: 'payee',
                text: t('Payee'),
              },
              {
                name: 'notes',
                text: t('Notes'),
              },
              {
                name: 'category',
                text: t('Category'),
              },
              // Add support later on until we have more user friendly amount input modal.
              // {
              //   name: 'amount',
              //   text: 'Amount',
              // },
              {
                name: 'cleared',
                text: t('Cleared'),
              },
            ]}
          />

          <Button
            variant="bare"
            aria-label={t('More options')}
            onPress={() => {
              setIsMoreOptionsMenuOpen(true);
            }}
            {...buttonProps}
          >
            <SvgDotsHorizontalTriple
              width={16}
              height={16}
              style={{ color: 'currentColor' }}
            />
          </Button>

          <HojaMenu
            isOpen={isMoreOptionsMenuOpen}
            titulo={t('More options')}
            subtitulo={textoSeleccion}
            iconos={{
              duplicate: 'plus',
              delete: 'trash',
              transfer: 'arrowupdown',
              merge: 'workflow',
            }}
            peligro={['delete']}
            onCerrar={() => setIsMoreOptionsMenuOpen(false)}
            onMenuSelect={type => {
              if (type === 'duplicate') {
                void onBatchDuplicate?.({
                  ids: selectedTransactionsArray,
                  onSuccess: ids => {
                    showUndoNotification({
                      message: t(
                        'Successfully duplicated {{count}} transactions.',
                        { count: ids.length },
                      ),
                    });
                  },
                });
              } else if (type === 'delete') {
                void onBatchDelete?.({
                  ids: selectedTransactionsArray,
                  onSuccess: ids => {
                    showUndoNotification({
                      type: 'warning',
                      message: t(
                        'Successfully deleted {{count}} transactions.',
                        { count: ids.length },
                      ),
                    });
                  },
                });
              } else if (type === 'transfer') {
                void onSetTransfer?.(selectedTransactionsArray, payees, ids =>
                  showUndoNotification({
                    message: t(
                      'Successfully marked {{count}} transactions as transfer.',
                      {
                        count: ids.length,
                      },
                    ),
                  }),
                );
              } else if (type === 'merge') {
                void onMerge?.(selectedTransactionsArray, () =>
                  showUndoNotification({
                    message: t('Successfully merged transactions'),
                  }),
                );
              }
              setIsMoreOptionsMenuOpen(false);
            }}
            items={moreOptionsMenuItems}
          />
        </View>
      </View>
    </FloatingActionBar>
  );
}
